// pages/monitor/panels/ems/data.js —— 能源管家面板局部演示数据（演示数据，待接口接入）
// 仅用于《云平台（能源管家）需求文档》2.7 单站监控界面评审；不修改公共数据层。

// 设备中断时附加的最后一次数据上报时间（文档 2.7.3 多处要求）
export const LAST_REPORT = '2026-10-17 09:40:00';

// PCS 四种状态（文档 2.7.3：四种状态，从 PCS 遥信中获取）
export const PCS_STATES = ['运行', '待机', '停机', '故障'];

// BMS 电堆 / 电池簇状态（文档 2.7.3：充电、放电、就绪、簇维护、禁充、禁放、故障）
export const BMS_STATES = ['充电', '放电', '就绪', '簇维护', '禁充', '禁放', '故障'];

// 自定义曲线可选测点（文档 2.7.5 自定义曲线：可添加不同测点）
export const CUSTOM_POINTS = [
  { key: 'grid', label: '电网功率', unit: 'kW', color: '#4dd8ff' },
  { key: 'load', label: '负载功率', unit: 'kW', color: '#ff5a7f' },
  { key: 'storage', label: '储能功率', unit: 'kW', color: '#52e8b0' },
  { key: 'soc', label: 'SOC', unit: '%', color: '#8b7cf6' },
  { key: 'price', label: '电价', unit: '¥/kWh', color: '#ffd166' },
];

// 自定义曲线导出频率（文档 2.7.5：支持选择不同频率的数据导出）
export const CUSTOM_FREQ = [
  { value: '15min', label: '15 分钟' },
  { value: '1h', label: '1 小时' },
  { value: '1d', label: '1 天' },
];

// 消防联动环顺序（文档 2.7.3：按 消防主机、温湿度计、水浸、烟感 顺序排列）
export const FIRE_LINK_RING = ['消防主机', '温湿度计', '水浸', '烟感'];

// 强电节点类型（文档 2.7.7 强电管理：公共连接点、变压器节点、储能节点、光伏节点、负载节点）
export const HV_NODE_TYPES = [
  { key: 'pcc', name: '公共连接点', desc: '指代电网与储能的并网点，可配置连接点名称、功率参考' },
  { key: 'transformer', name: '变压器节点', desc: '映射实际变压器设备，可配置功率参考、电气级别上下限、转换效率' },
  { key: 'storage', name: '储能节点', desc: '映射单个或一组充放行为一致的 PCS 及其电池堆，可绑定 PCS 支路与电池簇' },
  { key: 'pv', name: '光伏节点', desc: '映射单个光伏设备，其可控性根据现场情况配置（本站未接入光伏）' },
  { key: 'load', name: '负载节点', desc: '映射单个或一组负载设备' },
];

// ---------- 曲线报表：近七日 / 近两日补充序列（演示数据，待接口接入） ----------
// PCS 趋势（近七日）
const PCS_DAY_POWER = [210, 180, -160, -200, 140, -180, 220];
export const PCS_TREND = Array.from({ length: 7 }, (_, i) => {
  const power = PCS_DAY_POWER[i];
  return {
    clock: `${i + 4}日`,
    power,
    temp: +(36 + (i >= 2 && i <= 5 ? 4 : 0) + (i % 2)).toFixed(1),
    voltage: 398 + (i % 4) * 0.4,
    current: +(Math.abs(power) > 0 ? 50 + Math.abs(power) * 0.5 : 10).toFixed(1),
  };
});

// 电池堆对比（近两日）
export const RACK_2D = Array.from({ length: 24 }, (_, i) => {
  const day = i < 12 ? '昨日' : '今日';
  const h = (i % 12) * 2;
  return {
    clock: `${day} ${String(h).padStart(2, '0')}:00`,
    tempMax: +(29 + Math.sin(i / 3) * 2).toFixed(1),
    tempMin: +(24 + Math.cos(i / 3) * 1.5).toFixed(1),
    voltage: +(706 + Math.sin(i / 4) * 10).toFixed(1),
    current: +(160 + Math.sin(i / 2.5) * 30).toFixed(1),
    soc: +(72 + Math.sin(i / 5) * 8).toFixed(1),
  };
});

// 电表止度（每日零时止度，止度 = 电能 / 变比）
export const METER_STOP = [
  { key: 1, day: '10-05', meter: '储能进线电表 M-02', energy: 1682.4, ratio: 200, stop: 8.412 },
  { key: 2, day: '10-06', meter: '储能进线电表 M-02', energy: 1740.8, ratio: 200, stop: 8.704 },
  { key: 3, day: '10-07', meter: '储能进线电表 M-02', energy: 1691.2, ratio: 200, stop: 8.456 },
  { key: 4, day: '10-08', meter: '储能进线电表 M-02', energy: 1753.6, ratio: 200, stop: 8.768 },
  { key: 5, day: '10-09', meter: '储能进线电表 M-02', energy: 1812.0, ratio: 200, stop: 9.060 },
];

// 单体曲线代表点（与公共数据层 getCellCurve 的取值一致）
export const CELL_PICKS = [1, 26, 52, 78, 104];

export const CELL_TEMP = Array.from({ length: 7 }, (_, d) => {
  const row = { clock: `${d + 4}日` };
  CELL_PICKS.forEach((n) => { row[`c${n}`] = +(27.5 + Math.sin((d + n) / 6) * 1.2).toFixed(1); });
  return row;
});

export const CELL_SOC = Array.from({ length: 7 }, (_, d) => {
  const row = { clock: `${d + 4}日` };
  CELL_PICKS.forEach((n) => { row[`c${n}`] = +(72 + Math.sin((d + n) / 5) * 8).toFixed(1); });
  return row;
});

